/**
 * Media Management - Moon Moon Agency
 * Handles video lazy loading with viewport-based logic
 */

/*********************************************************************************************
 * 🎥 VIDEO MODULE
 */
const VideoManager = {
	// Local configuration
	config: {
		selectors: {
			videoLazy: '.mm-video-lazy',
			videoSource: 'source',
			playButton: '[data-video-toggle]',
			playIcon: '.mm-video-play-btn__play',
			pauseIcon: '.mm-video-play-btn__pause'
		},
		classes: {
			videoLoaded: 'mm-video-loaded',
			hidden: 'mm-hidden'
		},
		timing: {
			resizeDebounce: 150
		}
	},

	/**
	 * Initialize lazy video loading
	 */
	init() {
		const videos = window.getElements(this.config.selectors.videoLazy);
		if (!videos.length) return;

		const loadVideo = (video) => {
			if (video.dataset.loaded !== 'true') {
				// All sources: without loop, video_tag outputs HLS (m3u8) then the mp4 fallback
				const sources = [...video.querySelectorAll(this.config.selectors.videoSource)].filter((source) => source.dataset.src);
				if (!sources.length) return;

				sources.forEach((source) => {
					source.src = source.dataset.src;
				});
				video.preload = 'metadata';
				video.dataset.loaded = 'true';

				video.load();

				// Only autoplay if there's no play button (manual control)
				const wrapper = video.closest('[class*="wrapper"]') || video.parentElement;
				const hasPlayButton = wrapper?.querySelector(this.config.selectors.playButton);

				if (!hasPlayButton) {
					video.play().catch((err) =>
						window.log('warn', 'VideoManager', 'Video auto-play failed', { error: err.message })
					);
				}
			}
		};

		const unloadVideo = (video) => {
			if (video.dataset.loaded === 'true') {
				video.pause();
				video.currentTime = 0;
				video.dataset.loaded = 'false';
			}
		};

		const handleVideoVisibility = (video) => {
			const isMobileView = window.isMobile();
			const isDesktop = video.classList.contains('mm-desktop');
			const isMobileVideo = video.classList.contains('mm-mobile');

			const shouldPlay = (!isDesktop && !isMobileVideo) ||
								(isDesktop && !isMobileView) ||
								(isMobileVideo && isMobileView);

			if (shouldPlay) {
				loadVideo(video);
				video.classList.add(this.config.classes.videoLoaded);
			} else {
				video.classList.remove(this.config.classes.videoLoaded);
			}
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleVideoVisibility(entry.target);
				} else {
					unloadVideo(entry.target);
				}
			});
		}, {
			rootMargin: '50px 0px',
			threshold: 0.1
		});

		videos.forEach((video) => observer.observe(video));

		// Resize handling
		const handleResize = window.debounce(() => {
			videos.forEach((video) => {
				if (video.classList.contains(this.config.classes.videoLoaded)) {
					handleVideoVisibility(video);
				}
			});
		}, this.config.timing.resizeDebounce);

		window.addEventListener('resize', handleResize);

		// Initialize play/pause buttons
		this.initPlayButtons();
	},

	/**
	 * Initialize play/pause buttons for videos
	 */
	initPlayButtons() {
		const buttons = window.getElements(this.config.selectors.playButton);
		if (!buttons.length) return;

		buttons.forEach((btn) => {
			const wrapper = btn.closest('[class*="wrapper"]') || btn.parentElement;
			if (!wrapper) return;

			const playIcon = btn.querySelector(this.config.selectors.playIcon);
			const pauseIcon = btn.querySelector(this.config.selectors.pauseIcon);

			const showPlayIcon = () => {
				playIcon?.classList.remove(this.config.classes.hidden);
				pauseIcon?.classList.add(this.config.classes.hidden);
			};

			const showPauseIcon = () => {
				playIcon?.classList.add(this.config.classes.hidden);
				pauseIcon?.classList.remove(this.config.classes.hidden);
			};

			const getActiveVideo = () => {
				const isMobileView = window.isMobile();
				const videos = wrapper.querySelectorAll('video');

				for (const video of videos) {
					const isDesktop = video.classList.contains('mm-desktop');
					const isMobile = video.classList.contains('mm-mobile');

					if ((!isDesktop && !isMobile) || (isDesktop && !isMobileView) || (isMobile && isMobileView)) {
						return video;
					}
				}
				return videos[0];
			};

			const setupVideoListeners = (video) => {
				if (video.dataset.playButtonSetup) return;
				video.dataset.playButtonSetup = 'true';

				video.addEventListener('play', showPauseIcon);
				video.addEventListener('pause', showPlayIcon);
				video.addEventListener('ended', showPlayIcon);

				// Sync initial state
				if (!video.paused) {
					showPauseIcon();
				}
			};

			// Button click handler
			btn.addEventListener('click', () => {
				const video = getActiveVideo();
				if (!video) return;

				if (video.paused) {
					video.play().catch(() => {});
				} else {
					video.pause();
				}
			});

			// Setup listeners for existing videos
			const video = getActiveVideo();
			if (video) {
				setupVideoListeners(video);
			}

			// Watch for videos being added (lazy loaded)
			const observer = new MutationObserver(() => {
				const video = getActiveVideo();
				if (video) {
					setupVideoListeners(video);
				}
			});
			observer.observe(wrapper, { childList: true, subtree: true });
		});
	}
};

/*********************************************************************************************
 * 🎯 GLOBAL MEDIA MANAGER
 */
const GlobalMediaManager = {
	/**
	 * Initialize video management
	 */
	init() {
		VideoManager.init();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.VideoManager = VideoManager;
window.GlobalMediaManager = GlobalMediaManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmInitVideoLazy = () => VideoManager.init();

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	GlobalMediaManager.init();
});